import type {
  AnswerPayload,
  IAnswer,
  IDefinitionSnapshot,
  IRenderState,
  IRepeatRow,
  IResponseState,
  ISnapshotUsage
} from '../api/contracts';
import { addressKey, addressOf } from './snapshot-view';
import { rowWriteColumns, tableLayout } from './table-layout';

/**
 * survey-api's rules, evaluated here so the form answers a click at once: a follow-up a rule opens
 * appears on the pick that opens it, not after the choice delay and a round trip (474 ms on the
 * local stack, 777 ms over 300 ms of network). **survey-api stays the judge.** This predicts the
 * render only — usage, section, option, table row and column visibility and requiredness — and
 * every entry it cannot evaluate, or evaluates otherwise than survey-api did on survey-api's own
 * answers, stays survey-api's (`predictRender`). Findings, computed answers, repeat rows, progress
 * and submit are never derived here.
 *
 * The semantics are survey-api's `EvaluationContext`, `PredicateEvaluator` and `ExprEvaluator`,
 * and move with them (README, "Mirrors of survey-api"): an excluded answer reads as absent (its own
 * or an ancestor's rule-hide, its section's, its table row's, an inactive repeat row, an orphaned
 * per-option sub); `visibility_default: hidden` is a floor on the render, not an exclusion;
 * requiredness follows `required_mode`; a target under a repeat is evaluated unscoped and then per
 * active row. Where survey-api runs one pass in dependency order, this repeats passes to a fixed
 * point, which settles the same for the acyclic definitions publish admits.
 *
 * **What this cannot know is unknown, never guessed**, in three-valued logic: a predicate kind
 * outside the set below, a quantified ref (`any` / `all`), a computed usage or cell (the compute
 * specs are not shipped), a tolerance, an entry two rules write (survey-api's schedule orders
 * them), and an answer whose exclusion this decides otherwise than survey-api's `isExcluded`. An
 * unknown spreads to what depends on it, and its entries stay survey-api's. A usage's path scope is
 * not on the wire either: survey-api's render of a path-scoped usage disagrees with this one, and
 * the self-check hands it back.
 *
 * Rejected: waiting for survey-api on every pick, which is what made the form feel slow; and a
 * whole mirror of the engine — computes, rows, findings — which needs data the wire does not carry
 * and would be a second judge of submit.
 */

/** true, false, or null where it cannot be known here. */
type Tri = boolean | null;

const not = (a: Tri): Tri => (a === null ? null : !a);
const and = (a: Tri, b: Tri): Tri => {
  if (a === false || b === false) return false;
  return a === null || b === null ? null : true;
};
const or = (a: Tri, b: Tri): Tri => {
  if (a === true || b === true) return true;
  return a === null || b === null ? null : false;
};

/** A value that cannot be read here — the read-side twin of `null` above. */
const UNKNOWN = Symbol('unknown');
type Read = AnswerPayload | undefined | typeof UNKNOWN;

/** The numbers survey-api compares as decimals, compared here to a hair. */
const EPSILON = 1e-9;

interface IRef {
  usage: string;
  instance?: Nullable<string>;
  cell?: Nullable<string>;
}

interface IExprTerm {
  ref?: IRef;
  const?: number | string;
  coef?: number | string;
}

interface IExpr {
  terms: IExprTerm[];
  blank?: 'absent' | 'zero';
}

/** A `set_state` case, or its `default`. */
interface IStateCase {
  when?: unknown;
  visibility?: 'visible' | 'hidden';
  required?: boolean;
}

interface IEffect {
  effectType: string;
  targetKind: string;
  targetUsage: Nullable<string>;
  targetSection: Nullable<string>;
  targetOptionCode: Nullable<string>;
  params: Nullable<{
    cases?: IStateCase[];
    default?: IStateCase;
    rows?: string[];
    columns?: string[];
  }>;
}

/** An answer as the engine reads it — survey-api's, with its `isExcluded`, or a draft. */
export type IEngineAnswer = Pick<IAnswer, 'usage' | 'instanceId' | 'instanceKey' | 'value'> &
  Partial<Pick<IAnswer, 'isExcluded'>>;

export interface IRuleEvaluation {
  /** Each render entry, true, false or unknown (`null`); keyed as `entry` spells them. */
  entries: Map<string, Tri>;
  /**
   * survey-api's answers whose exclusion this evaluation decides otherwise than their own
   * `isExcluded` says, or cannot decide — a path-scoped answer off its path, typically. Read as
   * unknown in a second evaluation (`settle`), and in every prediction over the same state.
   */
  misread: Set<string>;
}

/** survey-api's `AnswerKeyOf`: where a usage's own answer lives — a per-option sub's at its
 *  trigger, any other at `''`. */
const ownKey = (usage: ISnapshotUsage) =>
  usage.childRole === 'per_option_sub' && usage.triggerOptionCode ? usage.triggerOptionCode : '';

/**
 * One entry of the render the engine decides, keyed so `predictRender` can compare it with
 * survey-api's: `u` a usage's visibility, `q` its requiredness (each flat, or per repeat row), `s`
 * a section's visibility, `o` an option's, `tr` / `tc` a table row's or column's, `tq` a table
 * row's requiredness.
 */
type EntryKind = 'u' | 'q' | 's' | 'o' | 'tr' | 'tc' | 'tq';
const entry = (kind: EntryKind, code: string, part?: Nullable<string>) =>
  part ? `${kind}|${code}|${part}` : `${kind}|${code}`;

const compare = (left: number, op: unknown, right: number): Tri => {
  const difference = left - right;
  const equal = Math.abs(difference) < EPSILON;
  switch (op) {
    case '==':
      return equal;
    case '!=':
      return !equal;
    case '>':
      return !equal && difference > 0;
    case '>=':
      return equal || difference > 0;
    case '<':
      return !equal && difference < 0;
    case '<=':
      return equal || difference < 0;
    default:
      return null;
  }
};

const numberOf = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * The render entries survey-api's rules give these answers, each true, false or unknown (`null`);
 * an answer in `distrusted` (by `addressKey`) reads as unknown.
 */
export function evaluateRules(
  definition: IDefinitionSnapshot,
  answers: readonly IEngineAnswer[],
  repeats: readonly IRepeatRow[],
  distrusted: ReadonlySet<string> = new Set()
): IRuleEvaluation {
  const usages = new Map(definition.usages.map((usage) => [usage.code, usage]));
  const parentOf = (usage: ISnapshotUsage) =>
    usage.parentUsage ? usages.get(usage.parentUsage) : undefined;

  const groups = new Map<string, Nullable<string>>();
  /** The repeat group a usage is under, or null outside one. */
  const groupOf = (usage: ISnapshotUsage): Nullable<string> => {
    if (groups.has(usage.code)) return groups.get(usage.code)!;
    let group: Nullable<string> = null;
    for (let above = parentOf(usage); above; above = parentOf(above)) {
      if (above.fieldType === 'repeat_group') {
        group = above.code;
        break;
      }
    }
    groups.set(usage.code, group);
    return group;
  };
  const rowsOf = new Map<string, string[]>();
  for (const row of [...repeats].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))) {
    if (row.status !== 'active') continue;
    rowsOf.set(row.usage, [...(rowsOf.get(row.usage) ?? []), row.instanceId]);
  }
  const activeRows = new Set([...rowsOf.values()].flat());
  const stored = new Map(
    answers.map((answer) => [
      addressKey(answer.usage, answer.instanceId, answer.instanceKey),
      answer.value
    ])
  );

  // What the rules wrote: `code|row` (row '' unscoped), a section, a table's `code|row` or
  // `code|column`, an option's `code|option`.
  const visible = new Map<string, Tri>();
  const required = new Map<string, Tri>();
  const sections = new Map<string, Tri>();
  const tableRows = new Map<string, Tri>();
  const tableColumns = new Map<string, Tri>();
  const tableRequired = new Map<string, Tri>();
  const options = new Map<string, Tri>();

  /** survey-api's `Resolve`: the row's entry, else the unscoped one, else the fallback. */
  const resolve = (map: Map<string, Tri>, code: string, row: Nullable<string>, fallback: Tri) => {
    if (row !== null && map.has(`${code}|${row}`)) return map.get(`${code}|${row}`)!;
    return map.has(`${code}|`) ? map.get(`${code}|`)! : fallback;
  };
  const sectionHidden = (section: Nullable<string>): Tri =>
    section === null ? false : not(sections.get(section) ?? true);

  const ruleHidden = (usage: ISnapshotUsage, row: Nullable<string>): Tri => {
    const parent = parentOf(usage);
    return or(
      or(not(resolve(visible, usage.code, row, true)), sectionHidden(usage.section)),
      parent ? ruleHidden(parent, row) : false
    );
  };
  const renderable = (usage: ISnapshotUsage, row: Nullable<string>): Tri => {
    if (usage.visibilityDefault !== 'visible') return false;
    const parent = parentOf(usage);
    return and(
      and(not(sectionHidden(usage.section)), resolve(visible, usage.code, row, true)),
      parent ? renderable(parent, row) : true
    );
  };

  /** survey-api's `IsOrphanedSub`: a per-option sub whose trigger its parent does not select, or
   *  anything under one. */
  const orphaned = (usage: ISnapshotUsage, key: string, row: Nullable<string>): Tri => {
    const parent = parentOf(usage);
    if (!parent) return false;
    if (usage.childRole === 'per_option_sub') {
      const selection = read(parent, row, ownKey(parent));
      if (selection === UNKNOWN) return null;
      return !(selection?.t === 'opts' && selection.v.includes(key));
    }
    return orphaned(parent, ownKey(parent), groupOf(parent) !== null ? row : null);
  };

  /** survey-api's `ExclusionCausesFor`, all but a path mismatch, which is not on the wire. */
  const excludedAt = (usage: ISnapshotUsage, row: Nullable<string>, key: string, table: boolean) =>
    or(
      or(ruleHidden(usage, row), table ? not(tableRows.get(`${usage.code}|${key}`) ?? true) : false),
      or(row !== null && !activeRows.has(row), orphaned(usage, key, row))
    );

  /** An answer as a rule reads it: absent when excluded, a table row without the cells it drops;
   *  a computed one is not known here, stored or not — survey-api computes it in the same pass. */
  function read(usage: ISnapshotUsage, row: Nullable<string>, key: string): Read {
    if (usage.isComputed) return UNKNOWN;
    const address = addressKey(usage.code, row, key);
    if (distrusted.has(address)) return UNKNOWN;
    const value = stored.get(address);
    if (value === undefined) return undefined;
    const isTable = value.t === 'cells';
    const excluded = excludedAt(usage, row, key, isTable);
    if (excluded === null) return UNKNOWN;
    if (excluded) return undefined;
    if (!isTable) return value;
    const layout = tableLayout(usage);
    if (layout.isHeading(key)) return undefined;
    const kept: Record<string, unknown> = {};
    for (const [column, cell] of Object.entries(value.v)) {
      const shown = tableColumns.get(`${usage.code}|${column}`) ?? true;
      if (shown === null) return UNKNOWN;
      if (shown && layout.kind(key, column) !== 'na') kept[column] = cell;
    }
    return Object.keys(kept).length > 0 ? ({ t: 'cells', v: kept } as AnswerPayload) : undefined;
  }

  /** survey-api's `Candidates` for an unquantified ref; null where a quantifier asks for more. */
  const candidates = (ref: IRef, row: Nullable<string>): AnswerPayload[] | null => {
    const usage = usages.get(ref.usage);
    if (!usage) return [];
    if (ref.instance === 'any' || ref.instance === 'all' || usage.isComputed) return null;
    // A computed cell is the engine's to work out in the same pass, whatever is stored.
    if (ref.cell && tableLayout(usage).kind(ref.instance ?? '', ref.cell) === 'computed') {
      return null;
    }
    let value: Read;
    if (groupOf(usage) !== null) {
      value = ref.instance == null && row !== null ? read(usage, row, ownKey(usage)) : undefined;
    } else {
      value = read(usage, null, ref.instance ?? ownKey(usage));
    }
    if (value === UNKNOWN) return null;
    return value === undefined ? [] : [value];
  };

  /** survey-api's `Numeric`: a scalar's number, or a numeric cell its `when` leaves open. */
  const numeric = (value: AnswerPayload, ref: IRef): number | undefined => {
    const usage = usages.get(ref.usage)!;
    if (!ref.cell) {
      return value.t === 'int' || value.t === 'num' || value.t === 'pct' || value.t === 'money'
        ? numberOf(value.v)
        : undefined;
    }
    if (value.t !== 'cells') return undefined;
    const columns = usage.params?.grid?.columns ?? usage.params?.multi_year?.columns ?? [];
    const column = columns.find((each) => each.code === ref.cell);
    if (!column) return undefined;
    const gate = column.when;
    if (gate && String(value.v[gate.cell] ?? '') !== String(gate.eq ?? '')) return undefined;
    const cell = value.v[column.code];
    return typeof cell === 'number' ? cell : undefined;
  };

  /** survey-api's `ExprEvaluator.Evaluate`: a number, absent (`undefined`) or unknown (`null`). */
  const evaluateExpr = (expr: IExpr, row: Nullable<string>): number | undefined | null => {
    let total = 0;
    let answerTerms = false;
    let answered = false;
    let present = false;
    for (const term of expr.terms ?? []) {
      const coef = numberOf(term.coef ?? 1);
      if (coef === undefined) return null;
      let value: number | undefined;
      if (term.const !== undefined) {
        value = numberOf(term.const);
        if (value === undefined) return null;
      } else if (term.ref) {
        answerTerms = true;
        const found = candidates(term.ref, row);
        if (found === null) return null;
        value = found.length > 0 ? numeric(found[0], term.ref) : undefined;
      } else {
        // A fact term is folded to a const before it ships; anything else is not known here.
        return null;
      }
      if (value === undefined) {
        if (expr.blank !== 'zero') return undefined;
        continue;
      }
      present = true;
      if (term.ref) answered = true;
      total += coef * value;
    }
    return (answerTerms ? answered : present) ? total : undefined;
  };

  const test = (node: unknown, row: Nullable<string>): Tri => {
    if (node === null || node === undefined) return true;
    if (typeof node !== 'object') return null;
    const parts = Object.entries(node as Record<string, unknown>);
    if (parts.length !== 1) return null;
    const [kind, arg] = parts[0] as [string, any];
    switch (kind) {
      case 'const':
        return typeof arg === 'boolean' ? arg : null;
      case 'not':
        return not(test(arg, row));
      case 'all':
        return Array.isArray(arg)
          ? arg.reduce<Tri>((so, each) => and(so, test(each, row)), true)
          : null;
      case 'any':
        return Array.isArray(arg)
          ? arg.reduce<Tri>((so, each) => or(so, test(each, row)), false)
          : null;
      case 'path_is':
        return definition.pathCode !== null && definition.pathCode === arg;
      case 'row_const':
        return row !== null && arg?.[row] === true;
      case 'answer_eq':
      case 'answer_in': {
        const found = candidates(arg.ref, row);
        if (found === null) return null;
        const wanted: string[] = kind === 'answer_eq' ? [arg.option] : arg.options ?? [];
        return found.some(
          (value) => value.t === 'opts' && value.v.some((code) => wanted.includes(code))
        );
      }
      case 'answer_cmp': {
        const found = candidates(arg.ref, row);
        const against = numberOf(arg.value);
        if (found === null || against === undefined) return null;
        let result: Tri = false;
        for (const value of found) {
          const number = numeric(value, arg.ref);
          if (number !== undefined) result = or(result, compare(number, arg.op, against));
        }
        return result;
      }
      case 'selected_count': {
        const found = candidates(arg.ref, row);
        const against = numberOf(arg.value);
        if (found === null || against === undefined) return null;
        if (found.length === 0) return compare(0, arg.op, against);
        return found.some(
          (value) => value.t === 'opts' && compare(value.v.length, arg.op, against) === true
        );
      }
      case 'expr_cmp': {
        if (arg.tolerance) return null;
        const left = evaluateExpr(arg.left, row);
        const right = evaluateExpr(arg.right, row);
        if (left === null || right === null) return null;
        return left === undefined || right === undefined ? false : compare(left, arg.op, right);
      }
      default:
        return null;
    }
  };

  /** The first case whose `when` holds, else the default; unknown once a case before it is. */
  const choose = (effect: IEffect, row: Nullable<string>): IStateCase | null | undefined => {
    for (const each of effect.params?.cases ?? []) {
      const holds = test(each.when, row);
      if (holds === null) return null;
      if (holds) return each;
    }
    return effect.params?.default;
  };
  const scopesOf = (usage: ISnapshotUsage): Nullable<string>[] => {
    const group = groupOf(usage);
    return group === null ? [null] : [null, ...(rowsOf.get(group) ?? [])];
  };
  // An entry two rules write is survey-api's: its schedule, which orders them by their inputs and
  // not by their codes, decides which writes last, and passes to a fixed point cannot.
  const writers = new Map<string, number>();
  const claim = (key: string) => writers.set(key, (writers.get(key) ?? 0) + 1);
  for (const rule of definition.rules) {
    for (const effect of rule.effects as IEffect[]) {
      const target = effect.targetUsage;
      if (effect.effectType === 'set_option_visibility' && target) {
        claim(`o|${target}|${effect.targetOptionCode}`);
      } else if (effect.effectType !== 'set_state' && effect.effectType !== 'set_visibility') {
        continue;
      } else if (effect.targetKind === 'section' && effect.targetSection) {
        claim(`s|${effect.targetSection}`);
      } else if (effect.targetKind === 'usage' && target) {
        const { rows, columns } = effect.params ?? {};
        if (!rows && !columns) claim(`u|${target}`);
        rows?.forEach((row) => claim(`tr|${target}|${row}`));
        columns?.forEach((column) => claim(`tc|${target}|${column}`));
      }
    }
  }
  /** A rule's outcome for one entry: unknown where another rule writes it too, and none — what an
   *  earlier pass wrote dropped — where no case matched and there is no default. */
  const write = (map: Map<string, Tri>, key: string, value: Tri | undefined, claimed: string) => {
    if (value === undefined) map.delete(key);
    else map.set(key, (writers.get(claimed) ?? 0) > 1 ? null : value);
  };
  const outcome = (chosen: IStateCase | null | undefined, dimension: 'visibility' | 'required') => {
    if (chosen === null) return null;
    if (!chosen || chosen[dimension] === undefined) return undefined;
    return dimension === 'visibility' ? chosen.visibility === 'visible' : chosen.required === true;
  };

  const apply = () => {
    for (const rule of definition.rules) {
      for (const effect of rule.effects as IEffect[]) {
        const usage = effect.targetUsage ? usages.get(effect.targetUsage) : undefined;
        if (effect.effectType === 'set_option_visibility' && usage && effect.targetOptionCode) {
          const key = `${usage.code}|${effect.targetOptionCode}`;
          write(options, key, test(rule.trigger, null), `o|${key}`);
        } else if (effect.targetKind === 'section' && effect.targetSection) {
          let shown: Tri | undefined;
          if (effect.effectType === 'set_visibility') {
            shown = test(rule.trigger, null);
          } else if (effect.effectType === 'set_state') {
            shown = outcome(choose(effect, null), 'visibility');
          }
          write(sections, effect.targetSection, shown, `s|${effect.targetSection}`);
        } else if (effect.targetKind === 'usage' && usage && effect.effectType === 'set_state') {
          const { rows, columns } = effect.params ?? {};
          if (rows || columns) {
            for (const row of rows ?? []) {
              const chosen = choose(effect, null);
              const key = `${usage.code}|${row}`;
              write(tableRows, key, outcome(chosen, 'visibility'), `tr|${key}`);
              write(tableRequired, key, outcome(chosen, 'required'), `tr|${key}`);
            }
            for (const column of columns ?? []) {
              const key = `${usage.code}|${column}`;
              write(tableColumns, key, outcome(choose(effect, null), 'visibility'), `tc|${key}`);
            }
            continue;
          }
          for (const row of scopesOf(usage)) {
            const chosen = choose(effect, row);
            const key = `${usage.code}|${row ?? ''}`;
            write(visible, key, outcome(chosen, 'visibility'), `u|${usage.code}`);
            write(required, key, outcome(chosen, 'required'), `u|${usage.code}`);
          }
        } else if (effect.targetKind === 'usage' && usage && effect.effectType === 'set_visibility') {
          for (const row of scopesOf(usage)) {
            write(visible, `${usage.code}|${row ?? ''}`, test(rule.trigger, row), `u|${usage.code}`);
          }
        }
      }
    }
  };

  const written = () =>
    [visible, required, sections, tableRows, tableColumns, tableRequired, options]
      .map((map) => [...map].join(';'))
      .join('#');
  let settled = false;
  for (let pass = 0; pass < 16 && !settled; pass++) {
    const before = written();
    apply();
    settled = written() === before;
  }

  const result = new Map<string, Tri>();
  const misread = new Set<string>();
  if (!settled) return { entries: result, misread };
  const requiredOf = (usage: ISnapshotUsage, row: Nullable<string>): Tri | undefined => {
    switch (usage.required) {
      case 'required':
        return true;
      case 'optional':
        return false;
      case 'required_when_visible':
        return renderable(usage, row);
      case 'by_rule':
        return resolve(required, usage.code, row, false);
      default:
        return undefined;
    }
  };
  for (const usage of definition.usages) {
    const group = groupOf(usage);
    const rows = group === null ? [] : (rowsOf.get(group) ?? []);
    // survey-api leaves a note out of the required maps.
    const asked = usage.fieldType !== 'display_note';
    if (rows.length === 0) {
      result.set(entry('u', usage.code), renderable(usage, null));
      const needed = requiredOf(usage, null);
      if (asked && needed !== undefined) result.set(entry('q', usage.code), needed);
      continue;
    }
    let anyShown: Tri = false;
    let anyRequired: Tri = false;
    for (const row of rows) {
      const shown = renderable(usage, row);
      anyShown = or(anyShown, shown);
      result.set(entry('u', usage.code, row), shown);
      if (!asked) continue;
      const needed = requiredOf(usage, row);
      if (needed === undefined) continue;
      anyRequired = or(anyRequired, needed);
      result.set(entry('q', usage.code, row), needed);
    }
    result.set(entry('u', usage.code), anyShown);
    if (asked && requiredOf(usage, null) !== undefined) {
      result.set(entry('q', usage.code), anyRequired);
    }
  }
  for (const [section, shown] of sections) result.set(entry('s', section), shown);
  for (const [key, shown] of options) result.set(`o|${key}`, shown);
  for (const [key, shown] of tableRows) result.set(`tr|${key}`, shown);
  for (const [key, shown] of tableColumns) result.set(`tc|${key}`, shown);
  for (const [key, needed] of tableRequired) result.set(`tq|${key}`, needed);
  for (const answer of answers) {
    const usage = usages.get(answer.usage);
    if (!usage || usage.isComputed || answer.isExcluded === undefined) continue;
    const table = answer.value.t === 'cells';
    const excluded = excludedAt(usage, answer.instanceId, answer.instanceKey, table);
    if (excluded !== answer.isExcluded) {
      misread.add(addressKey(answer.usage, answer.instanceId, answer.instanceKey));
    }
  }
  return { entries: result, misread };
}

/** survey-api's value of one entry, read with the defaults the render's readers apply. */
function served(render: IRenderState, key: string): boolean | undefined {
  const [kind, code, part] = key.split('|');
  switch (kind as EntryKind) {
    case 'u':
      return part
        ? render.usageVisibilityByInstance?.[code]?.[part]
        : render.usageVisibility[code] !== false;
    case 'q':
      return part ? render.requiredByInstance?.[code]?.[part] : render.required[code] === true;
    case 's':
      return render.sectionVisibility[code] !== false;
    case 'o':
      return render.optionVisibility[code]?.[part] !== false;
    case 'tr':
      return render.tableVisibility?.[code]?.rows?.[part];
    case 'tc':
      return render.tableVisibility?.[code]?.columns?.[part];
    case 'tq':
      return render.tableRequired?.[code]?.rows?.[part];
  }
}

/** One entry written into a copy of the render. */
function writeEntry(render: IRenderState, key: string, value: boolean) {
  const [kind, code, part] = key.split('|');
  switch (kind as EntryKind) {
    case 'u':
      if (part) {
        const rows = (render.usageVisibilityByInstance ??= {});
        rows[code] = { ...rows[code], [part]: value };
      } else render.usageVisibility[code] = value;
      break;
    case 'q':
      if (part) {
        const rows = (render.requiredByInstance ??= {});
        rows[code] = { ...rows[code], [part]: value };
      } else render.required[code] = value;
      break;
    case 's':
      render.sectionVisibility[code] = value;
      break;
    case 'o':
      render.optionVisibility[code] = { ...render.optionVisibility[code], [part]: value };
      break;
    case 'tr':
    case 'tc': {
      const table = ((render.tableVisibility ??= {})[code] ??= {});
      const axis = kind === 'tr' ? 'rows' : 'columns';
      table[axis] = { ...table[axis], [part]: value };
      break;
    }
    case 'tq': {
      const table = ((render.tableRequired ??= {})[code] ??= {});
      table.rows = { ...table.rows, [part]: value };
      break;
    }
  }
}

interface ISettled {
  entries: Map<string, Tri>;
  /** Entries this engine decides otherwise than survey-api did, on survey-api's own answers. */
  disagreements: Map<string, { served: boolean | undefined; evaluated: boolean }>;
  /** survey-api's answers read as unknown here — `IRuleEvaluation.misread`, gathered. */
  distrusted: Set<string>;
}

// One evaluation per state survey-api answered, however many drafts are laid over it.
const settledOf = new WeakMap<IResponseState, ISettled>();

function settle(definition: IDefinitionSnapshot, state: IResponseState): ISettled {
  const known = settledOf.get(state);
  if (known) return known;
  // An answer misread once is read as unknown, which may leave others undecided: a few rounds
  // gather them all.
  const distrusted = new Set<string>();
  let evaluation = evaluateRules(definition, state.answers, state.repeats);
  for (let round = 0; round < 4 && evaluation.misread.size > 0; round++) {
    const before = distrusted.size;
    evaluation.misread.forEach((key) => distrusted.add(key));
    if (distrusted.size === before) break;
    evaluation = evaluateRules(definition, state.answers, state.repeats, distrusted);
  }
  const { entries } = evaluation;
  const disagreements: ISettled['disagreements'] = new Map();
  for (const [key, evaluated] of entries) {
    if (evaluated === null) continue;
    const value = served(state.render, key);
    if (value !== evaluated) disagreements.set(key, { served: value, evaluated });
  }
  const settled = { entries, disagreements, distrusted };
  settledOf.set(state, settled);
  return settled;
}

/**
 * The entries this engine and survey-api decide differently on survey-api's own answers — read
 * as `entry: survey-api …, here …` — for the log. survey-api's value stands for each.
 */
export function ruleDisagreements(definition: IDefinitionSnapshot, state: IResponseState): string[] {
  return [...settle(definition, state).disagreements].map(
    ([key, { served, evaluated }]) => `${key}: survey-api ${served ?? 'absent'}, here ${evaluated}`
  );
}

/**
 * The render to draw: survey-api's, with every entry the unsaved answers move written as the rules
 * decide it — `overlay` is the drafts as payloads by address (`addressKey`), `null` for a field
 * emptied. An entry moves only where this engine, on survey-api's own answers, decided exactly what
 * survey-api did; anywhere else survey-api's value stands. With nothing unsaved, or nothing a draft
 * moves, it is survey-api's render itself.
 */
export function predictRender(
  definition: IDefinitionSnapshot,
  state: IResponseState,
  overlay: ReadonlyMap<string, Nullable<AnswerPayload>>
): IRenderState {
  if (overlay.size === 0) return state.render;
  const settled = settle(definition, state);
  const usages = new Map(definition.usages.map((usage) => [usage.code, usage]));

  const answers = new Map<string, IEngineAnswer>(
    state.answers.map((answer) => [
      addressKey(answer.usage, answer.instanceId, answer.instanceKey),
      answer
    ])
  );
  for (const [key, value] of overlay) {
    const address = addressOf(key);
    const usage = usages.get(address.usage);
    if (!usage) continue;
    if (value === null) {
      answers.delete(key);
      continue;
    }
    // A row draft holds the cells the respondent writes; the row's other cells are as stored.
    const before = answers.get(key)?.value;
    let next = value;
    if (value.t === 'cells' && before?.t === 'cells') {
      const cells = { ...before.v };
      for (const column of rowWriteColumns(usage, address.instanceKey)) delete cells[column.code];
      next = { t: 'cells', v: { ...cells, ...value.v } };
    }
    answers.set(key, { ...address, value: next });
  }

  const moved = evaluateRules(definition, [...answers.values()], state.repeats, settled.distrusted);
  let render: Nullable<IRenderState> = null;
  for (const [key, value] of moved.entries) {
    if (value === null) continue;
    const was = settled.entries.get(key);
    if (was === value || was === null || was === undefined || settled.disagreements.has(key)) {
      continue;
    }
    render ??= structuredClone(state.render);
    writeEntry(render, key, value);
  }
  return render ?? state.render;
}
